import React, { useState, useEffect, useCallback } from 'react';
import { Level, Question } from '../types';
import { 
  calculateScore, 
  calculateStars, 
  calculateAccuracy,
  isChallengePassed,
  formatTime
} from '../utils';

interface QuizPageProps {
  level: Level;
  questions: Question[];
  onComplete: (result: any) => void;
  onExit: () => void;
}

const QuizPage: React.FC<QuizPageProps> = ({
  level,
  questions,
  onComplete,
  onExit
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | string[] | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(90);
  const [totalTime, setTotalTime] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);

  useEffect(() => {
    setTotalTime(prev => prev + 1);
  }, [timeLeft]);

  const currentQuestion = questions[currentIndex];

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleNext();
          return 90;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentIndex]);

  const handleSelectAnswer = (answer: string) => {
    if (isAnswered) return;
    
    setSelectedAnswer(answer);
  };

  const handleSubmit = useCallback(() => {
    if (!selectedAnswer || isAnswered) return;

    setIsAnswered(true);
    setShowExplanation(true);
    
    const correct = currentQuestion.correctAnswer;
    let isAnswerCorrect = false;

    // 如果是选择题（有options），提取选项字母
    if (currentQuestion.options && Array.isArray(currentQuestion.options)) {
      if (Array.isArray(correct)) {
        // 多选题
        const selected = Array.isArray(selectedAnswer) ? selectedAnswer : [selectedAnswer];
        // 如果用户选择的是完整选项文字（如"A. 甲烷"），提取字母
        const selectedLetters = selected.map(s => {
          const letterMatch = String(s).match(/^[A-Z]/);
          return letterMatch ? letterMatch[0] : String(s);
        });
        const correctLetters = correct;
        
        isAnswerCorrect = selectedLetters.length === correctLetters.length && 
          selectedLetters.every(a => correctLetters.includes(a)) &&
          correctLetters.every(a => selectedLetters.includes(a));
      } else {
        // 单选题
        // 如果用户选择的是完整选项文字，提取字母
        let userAnswer = selectedAnswer;
        const letterMatch = String(selectedAnswer).match(/^[A-Z]/);
        if (letterMatch) {
          userAnswer = letterMatch[0];
        }
        
        // 如果正确答案是完整选项，也提取字母
        let correctAnswer = correct;
        const correctMatch = String(correct).match(/^[A-Z]/);
        if (correctMatch) {
          correctAnswer = correctMatch[0];
        }
        
        isAnswerCorrect = userAnswer === correctAnswer;
      }
    } else {
      // 非选择题（填空题、判断题）
      if (Array.isArray(correct)) {
        const selected = Array.isArray(selectedAnswer) ? selectedAnswer : [String(selectedAnswer).trim()];
        isAnswerCorrect = selected.length === correct.length && 
          selected.every((a, i) => String(a).trim() === String(correct[i]).trim()) &&
          correct.every((c, i) => String(selected[i]).trim() === String(c).trim());
      } else {
        // 字符串比较，忽略空格和大小写
        const userAnswer = String(selectedAnswer).trim().toLowerCase();
        const correctAnswer = String(correct).trim().toLowerCase();
        isAnswerCorrect = userAnswer === correctAnswer;
      }
    }

    setIsCorrect(isAnswerCorrect);
    if (isAnswerCorrect) {
      setCorrectCount(prev => prev + 1);
    }
  }, [selectedAnswer, isAnswered, currentQuestion]);

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
      setIsCorrect(false);
      setShowExplanation(false);
      setTimeLeft(90);
    } else {
      const finalScore = calculateScore(
        correctCount + (isCorrect ? 0 : 0),
        questions.length,
        totalTime,
        level.difficulty
      );
      const accuracy = calculateAccuracy(
        correctCount,
        questions.length
      );
      const stars = calculateStars(accuracy);
      const passed = isChallengePassed(correctCount, questions.length);

      onComplete({
        levelId: level.id,
        score: finalScore,
        correctCount: correctCount,
        totalQuestions: questions.length,
        timeSpent: totalTime,
        stars: stars,
        passed: passed,
        accuracy: accuracy
      });
    }
  };

  const getQuestionTypeLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      'single': '单选题',
      'multiple': '多选题',
      'fill': '填空题',
      'judge': '判断题',
      'experiment': '实验题',
      'calculation': '计算题',
      'reasoning': '推理题'
    };
    return labels[type] || '选择题';
  };

  const renderOptions = () => {
    if (!currentQuestion.options || currentQuestion.options.length === 0) {
      return (
        <textarea
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '12px',
            borderRadius: '8px',
            border: '2px solid #e5e7eb',
            fontSize: '16px',
            resize: 'vertical',
            marginTop: '16px'
          }}
          placeholder="请输入答案..."
          value={selectedAnswer as string || ''}
          onChange={(e) => setSelectedAnswer(e.target.value)}
        />
      );
    }

    // 提取选项字母的函数
    const getOptionLetter = (option: string): string => {
      const match = option.match(/^[A-Z]/);
      return match ? match[0] : option;
    };

    // 提取正确答案字母
    const correctAnswer = currentQuestion.correctAnswer;
    const correctLetters = Array.isArray(correctAnswer) 
      ? correctAnswer 
      : [getOptionLetter(String(correctAnswer))];

    // 提取用户选择的字母
    const selectedLetters = Array.isArray(selectedAnswer)
      ? selectedAnswer.map(s => getOptionLetter(s))
      : selectedAnswer ? [getOptionLetter(String(selectedAnswer))] : [];

    return (
      <div className="options-list">
        {currentQuestion.options.map((option, index) => {
          const optionLetter = getOptionLetter(option);
          let className = 'option-item';
          
          if (isAnswered) {
            className += ' disabled';
            // 如果是正确答案
            if (correctLetters.includes(optionLetter)) {
              className += ' correct';
            } else if (selectedLetters.includes(optionLetter)) {
              // 用户选择了但不是正确答案
              className += ' incorrect';
            }
          } else if (selectedLetters.includes(optionLetter)) {
            className += ' selected';
          }

          return (
            <div
              key={index}
              className={className}
              onClick={() => handleSelectAnswer(option)}
            >
              {option}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fade-in">
      {/* 顶部进度条 */}
      <div style={{
        background: 'white',
        padding: '12px 20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={onExit}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '16px',
            color: '#6b7280',
            cursor: 'pointer'
          }}
        >
          ✕
        </button>
        
        <div style={{ flex: 1, margin: '0 16px' }}>
          <div style={{
            background: '#e5e7eb',
            borderRadius: '8px',
            height: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${((currentIndex + 1) / questions.length) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#6b7280',
            marginTop: '4px'
          }}>
            {currentIndex + 1} / {questions.length}
          </div>
        </div>

        <div className="timer-display" style={{ fontSize: '20px' }}>
          {formatTime(timeLeft)}
        </div>
      </div>

      <div className="main-content">
        {/* 题目信息 */}
        <div style={{ marginBottom: '16px' }}>
          <span className="question-type">{getQuestionTypeLabel(currentQuestion.type)}</span>
          <span style={{
            marginLeft: '8px',
            padding: '4px 12px',
            background: '#f3f4f6',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#6b7280'
          }}>
            {currentQuestion.knowledgePoint}
          </span>
        </div>

        {/* 题目内容 */}
        <div className="question-card">
          <div className="question-content">
            {currentQuestion.content}
          </div>
          
          {currentQuestion.image && (
            <div style={{
              margin: '16px 0',
              textAlign: 'center'
            }}>
              <img 
                src={currentQuestion.image}
                alt="题目配图"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          )}

          {renderOptions()}
        </div>

        {/* 解析 */}
        {showExplanation && (
          <div className="question-card" style={{
            background: isCorrect ? '#d1fae5' : '#fee2e2',
            border: `2px solid ${isCorrect ? '#10b981' : '#ef4444'}`
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '8px',
              color: isCorrect ? '#065f46' : '#991b1b'
            }}>
              {isCorrect ? '✅ 回答正确！' : '❌ 回答错误'}
            </div>
            
            {!isCorrect && (
              <div style={{
                fontSize: '14px',
                marginBottom: '12px',
                color: '#6b7280'
              }}>
                正确答案：{Array.isArray(currentQuestion.correctAnswer) 
                  ? currentQuestion.correctAnswer.join('、') 
                  : currentQuestion.correctAnswer}
              </div>
            )}
            
            <div style={{
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#374151'
            }}>
              <strong>💡 解析：</strong>
              {currentQuestion.explanation}
            </div>

            {currentQuestion.tags && currentQuestion.tags.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                {currentQuestion.tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      background: 'rgba(255, 255, 255, 0.6)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#374151',
                      marginRight: '8px',
                      marginBottom: '4px'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="action-buttons">
          {!isAnswered ? (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!selectedAnswer}
              style={{ flex: 1 }}
            >
              提交答案
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleNext}
              style={{ flex: 1 }}
            >
              {currentIndex < questions.length - 1 ? '下一题 →' : '查看结果'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
